import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EarlierLead, LeadDto, LeadNoteDto, LeadStatus, LinkedClient } from '@mys/contracts';
/* From `/constants`, never from the package index: one runtime import off the
   index drags Zod into this bundle. Type-only imports above are free. */
import { isOpenLead, LEAD_STATUSES as CONTRACT_LEAD_STATUSES } from '@mys/contracts/constants';
import { api, ApiError, apiUrl } from './api.js';

/* ============================================================================
   Lead queries.
   ----------------------------------------------------------------------------
   Kept out of the components so the fetching and the rendering can be read,
   and changed, separately.
   ========================================================================== */

/**
 * The pipeline, from the contract rather than re-typed here.
 *
 * It was a second hand-written list until slice 1.5 widened the first one, at
 * which point this board would have rendered five columns of a seven-stage
 * pipeline — and a lead at `brief` would have vanished from it entirely,
 * because `groupByStatus` had no bucket to put it in and the card simply never
 * appeared. Nothing would have errored.
 */
export const LEAD_STATUSES: LeadStatus[] = [...CONTRACT_LEAD_STATUSES];

/** What each column is actually for, in the owner's words. The dashboard is
    English. `quoted` is stored under that name and shown as "Quote sent" —
    the label is the screen's, the value is the database's. */
export const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  brief: 'Brief received',
  preview: 'Preview sent',
  quoted: 'Quote sent',
  won: 'Won',
  lost: 'Lost',
};

export interface LeadListResponse {
  ok: true;
  items: LeadDto[];
  total: number;
  page: number;
  limit: number;
  counts: Partial<Record<LeadStatus, number>>;
}

export interface LeadDetailResponse {
  ok: true;
  lead: LeadDto;
  notes: LeadNoteDto[];
  /** Other enquiries from the same number, newest first, excluding this one.
      OPTIONAL in the type because the API and this dashboard do not deploy
      together: for the minutes between the two, a live build can be talking
      to a version that has never heard of it. The screen reads it as `?? []`
      for the same reason. */
  earlier?: EarlierLead[];
  /** The client this lead became, or null. Lets the screen offer one action
      instead of offering both and letting the API answer 409. */
  client: LinkedClient | null;
}

export interface LeadFilters {
  status?: LeadStatus;
  q?: string;
  /** An admin id, or `me`, or `none` for the unassigned pile. `me` is resolved
      by the API against the session, so a bookmarked link shows the leads of
      whoever opens it. */
  assignedTo?: string;
  /** Past the two-hour promise with nothing recorded. */
  overdue?: boolean;
}

/** @param limit omitted by the CSV export, which is not paginated — the API
    refuses an oversized one rather than quietly returning a page of it. */
function toQueryString(filters: LeadFilters, limit?: number): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.q) params.set('q', filters.q);
  if (filters.assignedTo) params.set('assignedTo', filters.assignedTo);
  /* Sent only when on. The API accepts `false` too, but a URL that carries
     every filter it is not using is one nobody can read in a bug report. */
  if (filters.overdue) params.set('overdue', 'true');
  if (limit !== undefined) params.set('limit', String(limit));
  return params.toString();
}

/**
 * Where the CSV lives, carrying whatever the board is currently filtered to.
 *
 * A plain link through `apiUrl()`, like the quote PDF — not a fetch into a
 * blob. The session is a cookie the browser attaches by itself, and
 * `admin.makeyoursite.in` and `api.makeyoursite.in` are the same SITE even
 * though they are different origins, so `SameSite=Strict` still sends it on a
 * top-level navigation. A bare `/api/...` href would open the DASHBOARD's own
 * host and 404 in production while working perfectly through the dev proxy,
 * which is why every link into the API goes through `apiUrl` and not a string.
 *
 * The cost of a link over a fetch: a refusal — the export cap, or a role that
 * may not — navigates to a JSON error page instead of showing a message. At
 * this business's volume the cap cannot be reached, and the button is only
 * rendered for the owner, so neither is reachable by accident. The quote PDF
 * has made the same trade since phase 7.
 */
export function leadsExportHref(filters: LeadFilters): string {
  const query = toQueryString(filters);
  return apiUrl(`/api/leads/export.csv${query ? `?${query}` : ''}`);
}

export interface Assignee {
  id: string;
  name: string;
}

/**
 * Who a lead can be handed to.
 *
 * Its own endpoint rather than the team list: `/api/settings/team` is
 * owner-only because it carries emails, roles and lock state, while assigning
 * a lead is something a salesperson does all day. This one is gated on
 * `leads:edit` and returns id and name.
 *
 * @param enabled false for an account without `leads:edit`, so a read-only
 *   viewer does not fire a request it knows answers 403 and then render the
 *   refusal as though something had gone wrong.
 */
export function useAssignees(enabled = true) {
  return useQuery({
    queryKey: ['lead-assignees'],
    queryFn: ({ signal }) =>
      api.get<{ ok: true; items: Assignee[] }>('/api/leads/assignees', signal),
    enabled,
    /* The team changes about twice a year. Refetching this on every mount of
       every lead page is a request per click for an answer that does not
       move. */
    staleTime: 5 * 60_000,
  });
}

/**
 * @param enabled false for an account without `leads:view`, so the overview
 *   does not fire a request it knows will answer 403 and then render the
 *   refusal as if something had gone wrong.
 */
export function useLeads(filters: LeadFilters, limit = 100, enabled = true) {
  return useQuery({
    queryKey: ['leads', filters, limit],
    queryFn: ({ signal }) =>
      api.get<LeadListResponse>(`/api/leads?${toQueryString(filters, limit)}`, signal),
    enabled,
  });
}

export function useLead(id: string) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: ({ signal }) => api.get<LeadDetailResponse>(`/api/leads/${id}`, signal),
  });
}

export interface LeadUpdate {
  status?: LeadStatus;
  note?: string;
  city?: string | null;
  businessType?: string | null;
  existingWebsite?: string | null;
  /** null unassigns. Absent leaves it alone — the PATCH distinction the API
      is built around, and the reason none of these carry a default. */
  assignedToId?: string | null;
  lostReason?: string | null;
}

export function useUpdateLead(id: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (changes: LeadUpdate) =>
      api.patch<{ ok: true; lead: LeadDto }>(`/api/leads/${id}`, changes),
    onSuccess: () => {
      /* Invalidate both: the board's counts change when a status does, and the
         detail page's note list changes when a note is added. Refetching is
         cheap here and getting the cache patching subtly wrong is not. */
      void client.invalidateQueries({ queryKey: ['leads'] });
      void client.invalidateQueries({ queryKey: ['lead', id] });
    },
  });
}

/**
 * The stages a lead can be moved to IN BULK, or by dropping it on a column.
 *
 * The five open ones. `won` and `lost` are left out of both, and it is one
 * rule stated once rather than two surfaces that happen to agree:
 *
 *   · **lost** is asked for a REASON, in the same request as the status. A
 *     bulk button is how fifty leads end up lost with no explanation, which
 *     is the one thing that field exists to prevent.
 *   · **won** is what converting to a client does, in a transaction that also
 *     creates the client row. A lead marked won with no client is a customer
 *     who does not exist.
 *
 * Derived from the pipeline minus the terminal pair, so a sixth open stage
 * joins both surfaces without an edit here.
 */
export const BULK_TARGET_STATUSES: LeadStatus[] = LEAD_STATUSES.filter((status) =>
  isOpenLead(status),
);

export interface BulkMoveResult {
  moved: number;
  /** The first lead that was refused, and why. Everything after it was left
      alone — see the sequential loop below. */
  failed: { business: string; message: string } | null;
}

/**
 * Move several leads to one status.
 *
 * **Sequential, and it stops at the first refusal**, exactly like the
 * permission matrix's save: each PATCH carries its own per-lead rules in the
 * service — the reply clock, the lost reason, the assignment check — so this
 * reuses all of them rather than inventing a bulk endpoint that would have to
 * repeat them. Stopping is what lets the failure be NAMED: "Moved 3. Patidar
 * Kirana was not moved — ...". Firing them all at once and counting the
 * rejections produces a number nobody can act on.
 */
export function useMoveLeads() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leads,
      status,
    }: {
      leads: Pick<LeadDto, 'id' | 'business'>[];
      status: LeadStatus;
    }): Promise<BulkMoveResult> => {
      let moved = 0;

      for (const lead of leads) {
        try {
          await api.patch<{ ok: true; lead: LeadDto }>(`/api/leads/${lead.id}`, { status });
          moved += 1;
        } catch (error) {
          return {
            moved,
            failed: {
              business: lead.business,
              message: error instanceof ApiError ? error.message : 'Could not move it.',
            },
          };
        }
      }

      return { moved, failed: null };
    },
    /* onSettled, not onSuccess: a run that stopped halfway still moved rows,
       and leaving the board showing the old columns after a partial move is
       how somebody moves the same lead twice. */
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * Group a flat list into kanban columns, preserving the server's ordering.
 *
 * The buckets are built FROM the pipeline rather than written out, so a stage
 * added to the contract gets a column here without anybody remembering to add
 * one. The literal version was missing `brief` and `preview` for as long as it
 * took to notice, and a lead with no bucket is not an error — the `?.` meant
 * the card was silently dropped from a board the owner reads to decide what to
 * do next.
 */
export function groupByStatus(leads: LeadDto[]): Record<LeadStatus, LeadDto[]> {
  const columns = Object.fromEntries(
    LEAD_STATUSES.map((status) => [status, [] as LeadDto[]]),
  ) as Record<LeadStatus, LeadDto[]>;
  for (const lead of leads) columns[lead.status]?.push(lead);
  return columns;
}

/** The next step in the pipeline, or null at either end. */
export function nextStatus(status: LeadStatus): LeadStatus | null {
  const index = LEAD_STATUSES.indexOf(status);
  // 'won' and 'lost' are both terminal, so nothing advances past 'quoted'.
  if (index < 0 || index >= LEAD_STATUSES.indexOf('won')) return null;
  return LEAD_STATUSES[index + 1] ?? null;
}

/** A one-tap WhatsApp reply, prefilled. The owner works on WhatsApp. */
export function waReplyHref(lead: Pick<LeadDto, 'phone' | 'business' | 'name'>): string {
  const text = [
    `Namaste ${lead.name ?? ''}`.trim() + ',',
    '',
    `${lead.business} ke liye aapki website enquiry mili hai.`,
    'Kab baat kar sakte hain?',
  ].join('\n');
  return `https://wa.me/${lead.phone.replace(/^\+/, '')}?text=${encodeURIComponent(text)}`;
}
